/**
 * Tenant Context Provider
 * Manages global state for tenant selection and list management.
 * Persists current tenant selection to localStorage.
 *
 * Pending B2B redemptions are automatically verified by the backend
 * when fetching the tenant list. Tenants appear in the accessible list
 * as soon as redemption is complete - no manual action required.
 */

/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { TenantApiService } from '../services/tenantApi';
import { ApiService } from '../services/api';
import type { TenantListItem, PendingTenant } from '../types/tenant';

/**
 * Tenant context type definition
 */
interface TenantContextType {
  currentTenant: TenantListItem | null;
  setCurrentTenant: (tenant: TenantListItem | null) => void;
  tenantList: TenantListItem[];
  pendingTenants: PendingTenant[];
  refreshTenantList: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

/**
 * Tenant context instance
 * Exported for testing purposes
 */
export const TenantContext = createContext<TenantContextType | undefined>(undefined);

/**
 * Local storage key for persisting selected tenant ID
 */
const SELECTED_TENANT_KEY = 'selectedTenantId';

/**
 * Tenant Provider Props
 */
interface TenantProviderProps {
  children: ReactNode;
}

/**
 * Tenant Context Provider Component
 * Wraps the application to provide tenant state and operations.
 *
 * Note: This provider is independent of authentication state.
 * The parent component (typically App.tsx) should call refreshTenantList()
 * when authentication is complete to trigger tenant data loading.
 *
 * @example
 * ```tsx
 * <TenantProvider>
 *   <App />
 * </TenantProvider>
 * ```
 */
export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const [currentTenant, setCurrentTenantState] = useState<TenantListItem | null>(null);
  const [tenantList, setTenantList] = useState<TenantListItem[]>([]);
  const [pendingTenants, setPendingTenants] = useState<PendingTenant[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Refresh tenant list from API
   * Memoized with useCallback to prevent infinite re-renders
   *
   * Backend filters tenants based on user role:
   * - Superadmins get all managed tenants
   * - Tenant admins get only their assigned tenant
   * - Regular users get empty list
   */
  const refreshTenantList = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await TenantApiService.listTenants({
        page: 1,
        page_size: 100, // Get all active tenants
      });
      setTenantList(response.items);
      setPendingTenants(response.pending_tenants);

      // Restore previously selected tenant if it still exists
      const savedTenantId = localStorage.getItem(SELECTED_TENANT_KEY);
      if (savedTenantId && !currentTenant) {
        const savedTenant = response.items.find(
          (t) => t.tenant_id === savedTenantId
        );
        if (savedTenant) {
          setCurrentTenantState(savedTenant);
          // Update API service with restored tenant context
          ApiService.setTenantContext(savedTenant.tenant_id);
        } else {
          // Clear invalid saved tenant ID
          localStorage.removeItem(SELECTED_TENANT_KEY);
        }
      }

      // Auto-select tenant for tenant admins (single-tenant users)
      // If user has exactly one tenant and no current selection, auto-select it
      if (response.items.length === 1 && !currentTenant && !savedTenantId) {
        const singleTenant = response.items[0];
        setCurrentTenantState(singleTenant);
        ApiService.setTenantContext(singleTenant.tenant_id);
        localStorage.setItem(SELECTED_TENANT_KEY, singleTenant.tenant_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tenant list');
    } finally {
      setIsLoading(false);
    }
  }, [currentTenant]); // Only recreate if currentTenant changes

  /**
   * Set current tenant and persist to localStorage
   */
  const setCurrentTenant = (tenant: TenantListItem | null) => {
    setCurrentTenantState(tenant);

    // Update API service with tenant context for X-Tenant-ID header
    ApiService.setTenantContext(tenant?.tenant_id || null);

    // Persist to localStorage
    if (tenant) {
      localStorage.setItem(SELECTED_TENANT_KEY, tenant.tenant_id);
    } else {
      localStorage.removeItem(SELECTED_TENANT_KEY);
    }
  };

  /**
   * Auto-refresh tenant list when window regains focus and there are pending tenants.
   * This handles the case where user completes consent in another tab/window and
   * returns to the app - the tenant list refreshes automatically to pick up the
   * newly-redeemed tenant.
   */
  const pendingTenantsRef = useRef(pendingTenants);
  pendingTenantsRef.current = pendingTenants;

  useEffect(() => {
    const handleFocus = () => {
      // Only refresh if there are pending tenants awaiting redemption
      if (pendingTenantsRef.current.length > 0) {
        refreshTenantList();
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshTenantList]);

  const value: TenantContextType = {
    currentTenant,
    setCurrentTenant,
    tenantList,
    pendingTenants,
    refreshTenantList,
    isLoading,
    error,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
};

/**
 * Hook to access tenant context
 * Must be used within TenantProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { currentTenant, setCurrentTenant } = useTenantContext();
 *   // Use tenant state...
 * }
 * ```
 *
 * @throws Error if used outside TenantProvider
 */
export const useTenantContext = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenantContext must be used within TenantProvider');
  }
  return context;
};
